import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, RevisionIdSchema, RunIdSchema, } from "../primitives/ids.js";
import { JsonValueSchema } from "./patch-operation.js";
const SnapshotHashSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * POST .../businessgraph/drafts/:draftId/confirm
 *
 * Idempotency: require `Idempotency-Key` HTTP header (not a body field).
 */
export const ConfirmDraftRequestSchema = z
    .object({
    schemaId: z.literal("step5.confirmDraftRequest.v1").optional(),
    expectedDraftVersion: z.number().int().min(1),
    expectedDraftSnapshotHash: SnapshotHashSchema,
    expectedPredecessorRevisionId: RevisionIdSchema.nullable(),
})
    .strict();
export const ConfirmDraftResponseSchema = z
    .object({
    schemaId: z.literal("step5.confirmDraftResponse.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    draftId: z.string().min(1),
    draftVersion: z.number().int().min(1),
    draftSnapshotHash: SnapshotHashSchema,
    confirmationId: z.string().min(1),
    candidateRevisionId: RevisionIdSchema,
    candidateSnapshotHash: SnapshotHashSchema,
    predecessorRevisionId: RevisionIdSchema.nullable(),
    confirmedAt: z.string().datetime(),
    /** Exact UI copy anchor — candidate is saved but not active. */
    message: z.literal("Confirmed business information candidate saved. It is not active yet."),
})
    .strict();
/**
 * POST .../businessgraph/drafts/initial
 *
 * Creates the first current draft tip (`currentBusinessGraphDraftId`) when none exists.
 * Idempotency: require `Idempotency-Key` HTTP header (not a body field).
 * Content may be supplied as `businessGraph` and/or flexible `content` JSON.
 */
export const CreateInitialDraftRequestSchema = z
    .object({
    schemaId: z.literal("step5.createInitialDraftRequest.v1").optional(),
    source: z.string().min(1).max(80).optional(),
    businessGraph: JsonValueSchema.optional(),
    content: JsonValueSchema.optional(),
})
    .strict()
    .refine((value) => value.businessGraph !== undefined || value.content !== undefined, {
    message: "businessGraph_or_content_required",
});
export const CreateInitialDraftResponseSchema = z
    .object({
    schemaId: z.literal("step5.createInitialDraftResponse.v1"),
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    draftId: z.string().min(1),
    draftVersion: z.number().int().min(1),
    draftSnapshotHash: SnapshotHashSchema,
    replayed: z.boolean(),
})
    .strict();
/**
 * POST .../businessgraph/drafts/replace
 *
 * Idempotency: require `Idempotency-Key` HTTP header (not a body field).
 * Content may be supplied as `businessGraph` and/or flexible `content` JSON.
 */
export const ReplaceDraftRequestSchema = z
    .object({
    schemaId: z.literal("step5.replaceDraftRequest.v1").optional(),
    expectedCurrentDraftId: z.string().min(1),
    expectedCurrentDraftVersion: z.number().int().min(1),
    expectedCurrentDraftSnapshotHash: SnapshotHashSchema,
    source: z.string().min(1),
    replacementReason: z.string().min(1).max(2000),
    businessGraph: JsonValueSchema.optional(),
    content: JsonValueSchema.optional(),
})
    .strict()
    .refine((value) => value.businessGraph !== undefined || value.content !== undefined, {
    message: "businessGraph_or_content_required",
});
export const StartBuildModeSchema = z.enum(["FRESH_BUILD", "INFRASTRUCTURE_RETRY"]);
/**
 * POST .../candidate-confirmations/:confirmationId/start-build
 *
 * Idempotency: require `Idempotency-Key` HTTP header (not a body field).
 */
export const StartBuildRequestSchema = z
    .object({
    schemaId: z.literal("step5.startBuildRequest.v1").optional(),
    mode: StartBuildModeSchema,
    expectedCurrentDraftId: z.string().min(1),
    expectedCurrentDraftVersion: z.number().int().min(1),
    expectedCurrentDraftSnapshotHash: SnapshotHashSchema,
    expectedPredecessorRevisionId: RevisionIdSchema.nullable(),
    expectedGenerationSelectionVersion: z.number().int().min(1),
    /** Required when mode is INFRASTRUCTURE_RETRY. */
    retryOfRunId: RunIdSchema.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.mode === "INFRASTRUCTURE_RETRY" && value.retryOfRunId === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "retryOfRunId_required_for_infrastructure_retry",
            path: ["retryOfRunId"],
        });
    }
    if (value.mode === "FRESH_BUILD" && value.retryOfRunId !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "retryOfRunId_forbidden_for_fresh_build",
            path: ["retryOfRunId"],
        });
    }
});
/**
 * PATCH .../projects/:projectId/delivery-due-at
 *
 * Idempotency: require `Idempotency-Key` HTTP header when used as a material mutation.
 */
export const DeliveryDueAtPatchRequestSchema = z
    .object({
    schemaId: z.literal("step5.deliveryDueAtPatchRequest.v1").optional(),
    deliveryDueAt: z.string().datetime().nullable(),
    expectedLifecycleVersion: z.number().int().min(0),
})
    .strict();
//# sourceMappingURL=step5-businessgraph.js.map